/**
 * Metrics Panel Component
 * Shows before/after comparison stats - critical for judges
 */

import { useFabStore } from '../store';

export function MetricsPanel() {
  const { currentStats, currentJob, isProcessing } = useFabStore();

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  const calculateReduction = (before: number, after: number): string => {
    if (before === 0) return '0%';
    const reduction = ((before - after) / before) * 100;
    return reduction.toFixed(1) + '%';
  };

  // Show placeholder when no stats
  if (!currentStats && !isProcessing) {
    return (
      <div className="bg-fab-dark border-t border-slate-700 px-6 py-4">
        <div className="flex items-center justify-center gap-8 text-slate-500">
          <div className="text-center">
            <div className="text-xs uppercase tracking-wide mb-1">Triangles</div>
            <div className="text-lg font-mono">-- → --</div>
          </div>
          <div className="text-center">
            <div className="text-xs uppercase tracking-wide mb-1">File Size</div>
            <div className="text-lg font-mono">-- → --</div>
          </div>
          <div className="text-center">
            <div className="text-xs uppercase tracking-wide mb-1">Texture</div>
            <div className="text-lg font-mono">--</div>
          </div>
          <div className="text-center">
            <div className="text-xs uppercase tracking-wide mb-1">Runtime</div>
            <div className="text-lg font-mono">--</div>
          </div>
        </div>
      </div>
    );
  }

  // Show processing state
  if (isProcessing) {
    return (
      <div className="bg-fab-dark border-t border-slate-700 px-6 py-4">
        <div className="flex items-center justify-center gap-4 text-fab-warning">
          <span className="animate-spin text-xl">⚙️</span>
          <span className="text-sm">Fabricating asset...</span>
          {currentJob && (
            <span className="text-xs text-slate-500">
              Job: {currentJob.job_id.slice(0, 8)}
            </span>
          )}
        </div>
      </div>
    );
  }

  // Show actual stats
  return (
    <div className="bg-fab-dark border-t border-slate-700 px-6 py-4">
      <div className="flex items-center justify-center gap-8">
        {/* Triangles */}
        <div className="text-center">
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
            Triangles
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-mono text-slate-300">
              {formatNumber(currentStats!.input_tris)}
            </span>
            <span className="text-fab-accent">→</span>
            <span className="text-lg font-mono text-fab-success">
              {formatNumber(currentStats!.output_tris)}
            </span>
          </div>
          <div className="text-xs text-fab-success">
            -{calculateReduction(currentStats!.input_tris, currentStats!.output_tris)}
          </div>
        </div>

        {/* File Size */}
        <div className="text-center">
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
            File Size
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-mono text-slate-300">
              {formatBytes(currentStats!.input_bytes)}
            </span>
            <span className="text-fab-accent">→</span>
            <span className="text-lg font-mono text-fab-success">
              {formatBytes(currentStats!.output_bytes)}
            </span>
          </div>
          <div className="text-xs text-fab-success">
            -{calculateReduction(currentStats!.input_bytes, currentStats!.output_bytes)}
          </div>
        </div>

        {/* Texture Resolution */}
        <div className="text-center">
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
            Texture
          </div>
          <div className="text-lg font-mono text-fab-accent">
            {currentStats!.texture_res}px
          </div>
          <div className="text-xs text-slate-500">baked albedo</div>
        </div>

        {/* Runtime */}
        <div className="text-center">
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
            Runtime
          </div>
          <div className="text-lg font-mono text-slate-300">
            {(currentStats!.runtime_ms / 1000).toFixed(1)}s
          </div>
          <div className="text-xs text-slate-500">Vultr GPU</div>
        </div>
      </div>
    </div>
  );
}
